import React, { useCallback, useEffect, useState } from 'react';
import { FiX, FiUserPlus } from 'react-icons/fi';

/**
 * Список заявок на регистрацию (модерация admin / HR).
 * @param {{ open: boolean, onClose: () => void, isMobile?: boolean, sidebarOffset?: number }} props
 */
export default function PendingRegistrationsModal({ open, onClose, isMobile = false, sidebarOffset = 320 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [twofaRequired, setTwofaRequired] = useState(true);
  const [savingRegistrationMode, setSavingRegistrationMode] = useState(false);
  const [savingTwofaMode, setSavingTwofaMode] = useState(false);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    setErr('');
    try {
      const r = await fetch('/api/admin/pending-registrations', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось загрузить список');
      setItems(Array.isArray(data.items) ? data.items : []);
      const regSettingsResp = await fetch('/api/admin/registration-settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const regData = await regSettingsResp.json().catch(() => ({}));
      if (regSettingsResp.ok) {
        setRegistrationEnabled(regData.registrationEnabled !== false);
        setTwofaRequired(regData.twofaRequired !== false);
      }
    } catch (e) {
      setErr(e.message || 'Ошибка');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleRegistration = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSavingRegistrationMode(true);
    setErr('');
    try {
      const nextValue = !registrationEnabled;
      const r = await fetch('/api/admin/registration-settings', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          registrationEnabled: nextValue,
          twofaRequired,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось сохранить настройку');
      setRegistrationEnabled(data.registrationEnabled !== false);
      if (data.twofaRequired !== undefined) setTwofaRequired(data.twofaRequired !== false);
    } catch (e) {
      setErr(e.message || 'Ошибка');
    } finally {
      setSavingRegistrationMode(false);
    }
  };

  const toggleTwofa = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSavingTwofaMode(true);
    setErr('');
    try {
      const nextValue = !twofaRequired;
      const r = await fetch('/api/admin/registration-settings', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          twofaRequired: nextValue,
          registrationEnabled,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось сохранить настройку 2FA');
      setTwofaRequired(data.twofaRequired !== false);
      if (data.registrationEnabled !== undefined) setRegistrationEnabled(data.registrationEnabled !== false);
      window.dispatchEvent(new Event('mesendger:2fa-policy-changed'));
    } catch (e) {
      setErr(e.message || 'Ошибка');
    } finally {
      setSavingTwofaMode(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const approve = async (userId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setBusyId(userId);
    setErr('');
    try {
      const r = await fetch(`/api/admin/pending-registrations/${userId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Ошибка одобрения');
      await load();
    } catch (e) {
      setErr(e.message || 'Ошибка');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (userId) => {
    if (!window.confirm('Отклонить заявку? Учётная запись будет удалена, повторная регистрация будет возможна.')) {
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setBusyId(userId);
    setErr('');
    try {
      const r = await fetch(`/api/admin/pending-registrations/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Ошибка отклонения');
      await load();
    } catch (e) {
      setErr(e.message || 'Ошибка');
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  const left = isMobile ? 0 : sidebarOffset;
  const width = isMobile ? '100vw' : `calc(100vw - ${sidebarOffset}px)`;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left,
        right: 0,
        bottom: 0,
        width,
        height: '100vh',
        background: 'rgba(0,0,0,0.55)',
        zIndex: 200004,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
          width: '100%',
          maxWidth: isMobile ? '100%' : 560,
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          color: '#e2e8f0',
          padding: '24px 22px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontWeight: 900, fontSize: '1.25em', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiUserPlus size={22} /> Заявки на регистрацию
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '1.5rem',
              padding: 4,
              borderRadius: 4,
            }}
            aria-label="Закрыть"
          >
            <FiX />
          </button>
        </div>
        <p style={{ margin: '0 0 16px 0', color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.45 }}>
          Новые сотрудники не могут войти, пока заявка не одобрена. После одобрения пользователь сможет войти с тем же паролем.
        </p>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ color: '#cbd5e1', fontSize: '0.92rem' }}>
            Самостоятельная регистрация: <strong style={{ color: registrationEnabled ? '#86efac' : '#fca5a5' }}>{registrationEnabled ? 'включена' : 'отключена'}</strong>
          </span>
          <button
            type="button"
            onClick={toggleRegistration}
            disabled={savingRegistrationMode || savingTwofaMode}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: registrationEnabled ? '1px solid rgba(239,68,68,0.45)' : '1px solid rgba(67,233,123,0.45)',
              background: registrationEnabled ? 'rgba(239,68,68,0.12)' : 'rgba(67,233,123,0.18)',
              color: registrationEnabled ? '#fecaca' : '#86efac',
              cursor: savingRegistrationMode || savingTwofaMode ? 'wait' : 'pointer',
              fontWeight: 700,
              opacity: savingRegistrationMode || savingTwofaMode ? 0.6 : 1,
            }}
          >
            {savingRegistrationMode ? 'Сохранение…' : registrationEnabled ? 'Отключить регистрацию' : 'Включить регистрацию'}
          </button>
        </div>
        <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ color: '#cbd5e1', fontSize: '0.92rem' }}>
            Обязательная 2FA для всех: <strong style={{ color: twofaRequired ? '#86efac' : '#fca5a5' }}>{twofaRequired ? 'включена' : 'отключена'}</strong>
          </span>
          <button
            type="button"
            onClick={toggleTwofa}
            disabled={savingTwofaMode || savingRegistrationMode}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: twofaRequired ? '1px solid rgba(239,68,68,0.45)' : '1px solid rgba(67,233,123,0.45)',
              background: twofaRequired ? 'rgba(239,68,68,0.12)' : 'rgba(67,233,123,0.18)',
              color: twofaRequired ? '#fecaca' : '#86efac',
              cursor: savingTwofaMode || savingRegistrationMode ? 'wait' : 'pointer',
              fontWeight: 700,
              opacity: savingTwofaMode || savingRegistrationMode ? 0.6 : 1,
            }}
          >
            {savingTwofaMode ? 'Сохранение…' : twofaRequired ? 'Отключить 2FA для всех' : 'Включить 2FA для всех'}
          </button>
        </div>
        <p style={{ margin: '0 0 14px 0', color: '#6b7280', fontSize: '0.85rem', lineHeight: 1.45 }}>
          Если отключить 2FA для всех, вход и работа в приложении без кода аутентификатора; при снова включённой политике пользователи без настроенной 2FA увидят экран настройки.
        </p>
        {err ? (
          <div style={{ color: '#fecaca', marginBottom: 12, fontSize: '0.95rem' }}>{err}</div>
        ) : null}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>Загрузка…</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#6b7280', fontSize: '1rem' }}>Нет ожидающих заявок</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {items.map((row) => {
              const fio = [row.first_name, row.last_name].filter(Boolean).join(' ') || row.username || '—';
              const dob =
                row.birth_day && row.birth_month && row.birth_year
                  ? `${row.birth_day}.${row.birth_month}.${row.birth_year}`
                  : '—';
              const uid = row.user_id;
              const disabled = busyId === uid;
              return (
                <li
                  key={uid}
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>{fio}</div>
                  <div style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: 4 }}>
                    Логин: <span style={{ color: '#e2e8f0' }}>{row.username}</span>
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: 4 }}>
                    Должность: <span style={{ color: '#e2e8f0' }}>{row.department || '—'}</span>
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: 10 }}>
                    Дата рождения: <span style={{ color: '#e2e8f0' }}>{dob}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => approve(uid)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '1px solid rgba(67,233,123,0.45)',
                        background: 'rgba(67,233,123,0.2)',
                        color: '#86efac',
                        cursor: disabled ? 'wait' : 'pointer',
                        fontWeight: 700,
                        opacity: disabled ? 0.6 : 1,
                      }}
                    >
                      Одобрить
                    </button>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => reject(uid)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 8,
                        border: '1px solid rgba(239,68,68,0.4)',
                        background: 'rgba(239,68,68,0.12)',
                        color: '#fecaca',
                        cursor: disabled ? 'wait' : 'pointer',
                        fontWeight: 600,
                        opacity: disabled ? 0.6 : 1,
                      }}
                    >
                      Отклонить
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
